import React, { useContext } from 'react';
import { Row, Col } from 'react-bootstrap';
import { numberFormatter } from '../../components/formatters/numberFormatter';
import { toWords } from 'number-to-words';
import { DataContext } from '../../init/getData';
import ReceiptVoucher from './receiptPrint';

// This is the main component to be printed
const ComponentToPrint = React.forwardRef((props, ref) => {
  const {
    journalVoucher,
    vatAmount,
    mainCurrency,
    secondaryCurrency,
    secondaryVatAmount,
    totalBeforeVat,
    secondaryTotalBeforeVat,
  } = props;

  const { settingData, userData } = useContext(DataContext);

  // ✅ if it's a Receipt, just return ReceiptVoucher
  if (journalVoucher?.type === 'Receipt') {
    return (
      <ReceiptVoucher
        ref={ref}
        journalVoucher={journalVoucher}
        vatAmount={vatAmount}
        mainCurrency={mainCurrency}
        secondaryCurrency={secondaryCurrency}
        secondaryVatAmount={secondaryVatAmount}
      />
    );
  }

  // ✅ Columns Definition
  const cartColumns = [
    {
      title: 'Description',
      dataIndex: 'product',
      width: '40%',
      render: (product) => product?.name,
    },
    {
      title: 'Unit',
      dataIndex: 'unitName',
      width: '12%',
      render: (unitName, record) =>
        unitName ? `${unitName} (${record.unitQuantity})` : '',
    },
    {
      title: 'Quantity',
      dataIndex: 'netQuantity',
      width: '9%',
      render: numberFormatter,
    },
    {
      title: 'Unit Price',
      dataIndex: 'price',
      width: '15%',
      render: numberFormatter,
    },
    {
      title: 'Discount',
      dataIndex: 'priceDiscountPercentage',
      width: '10%',
      render: numberFormatter,
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      width: '15%',
      render: numberFormatter,
    },
  ];

  const cartItems = journalVoucher?.cart || [];
  const totalItems = cartItems.length; // ✅ NEW: Get total item count

  // --- Pagination Logic ---
  // This is required for the "stretch-to-fill" layout to work on each page.
  // You can adjust this number.
  const ITEMS_PER_PAGE = 15;
  const MIN_ROWS = 10; // ✅ NEW: Define your minimum row requirement

  // Utility function for chunking the array
  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const paginatedItems = chunkArray(cartItems, ITEMS_PER_PAGE);

  // If there are no items, we still render ONE page
  const pagesToRender = paginatedItems.length > 0 ? paginatedItems : [[]];
  const totalPages = pagesToRender.length;
  // --- End of Pagination ---

  return (
    // ✅ Main Print Wrapper
    <div ref={ref}>
      {pagesToRender.map((pageItems, pageIndex) => {
        const isLastPage = pageIndex === totalPages - 1;

        // ✅ NEW: Calculate blank rows needed
        let blankRowCount = 0;
        // Only apply this logic on the last page
        if (isLastPage) {
          // And only if the *total* number of items is less than MIN_ROWS
          if (totalItems < MIN_ROWS) {
            // pageItems.length will equal totalItems here (since 10 < 15)
            blankRowCount = MIN_ROWS - pageItems.length;
          }
        }
        
        return (
          // ✅ Page Container
          <div
            key={pageIndex}
            className="print-page"
            style={{
              padding: '25px',
              fontFamily: 'Arial, sans-serif',
              color: '#333',
              minHeight: '98vh', // Fills the printable area
              display: 'flex',
              flexDirection: 'column',
              pageBreakAfter: isLastPage ? 'auto' : 'always',
            }}
          >
            {/* ✅ Header (Repeats on every page) */}
            <div
              style={{
                marginBottom: '0px',
                borderBottom: '1px solid #ddd',
                paddingBottom: '6px',
                flexShrink: 0, // Prevents header from shrinking
              }}
            >
              <Row style={{ marginBottom: '0' }}>
                {/* LEFT SIDE */}
                <Col md={7}>
                  <h2
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.3rem',
                      margin: '0 0 4px 0',
                    }}
                  >
                    {journalVoucher?.company?.name}
                  </h2>
                  <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                    <strong style={{ display: 'inline-block', width: '70px' }}>
                      Reg:
                    </strong>
                    {journalVoucher?.company?.registrationNumber}
                  </p>
                  <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                    <strong style={{ display: 'inline-block', width: '70px' }}>
                      Address:
                    </strong>
                    {journalVoucher?.company?.address}
                  </p>
                  <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                    <strong style={{ display: 'inline-block', width: '70px' }}>
                      Phone:
                    </strong>
                    {journalVoucher?.company?.phone}
                  </p>
                </Col>

                {/* RIGHT SIDE */}
                <Col md={5} style={{ textAlign: 'right' }}>
                  <div style={{ textAlign: 'left', display: 'inline-block' }}>
                    <h2
                      style={{
                        fontSize: '1.3rem',
                        margin: '0 0 6px 0',
                        fontWeight: 'bold',
                      }}
                    >
                      {journalVoucher?.type === 'Receipt'
                        ? 'RECEIPT'
                        : journalVoucher?.type === 'Payment'
                        ? 'PAYMENT'
                        : journalVoucher?.type === 'PosOrder'
                        ? 'ORDER'
                        : 'INVOICE'}
                    </h2>

                    <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                      <strong style={{ display: 'inline-block', width: '40px' }}>
                        ID:
                      </strong>
                      {journalVoucher?.myId}
                    </p>

                    <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                      <strong style={{ display: 'inline-block', width: '40px' }}>
                        Date:
                      </strong>
                      {journalVoucher?.date}
                    </p>

                    <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                      <strong style={{ display: 'inline-block', width: '70px' }}>
                        Currency:
                      </strong>{' '}
                      {userData?.role?.defaultCurrency?.code}
                    </p>

                    <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                      <strong>Rate:</strong>{' '}
                      {numberFormatter(journalVoucher?.currency?.rate)}{' '}
                      {journalVoucher?.currency?.symbol ||
                        mainCurrency?.symbol ||
                        ''}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>

            {/* ✅ Billed To Section (Only on first page) */}
            {pageIndex === 0 && (
              <div style={{ marginTop: '20px', flexShrink: 0 }}>
                <div
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '0px',
                    padding: '6px 10px',
                    backgroundColor: 'transparent',
                  }}
                >
                  <Row style={{ marginBottom: '0' }}>
                    <Col md={6}>
                      <div style={{ padding: '0', border: 'none' }}>
                        <h3
                          style={{
                            fontSize: '1rem',
                            margin: '0 0 4px 0',
                            fontWeight: 'bold',
                          }}
                        >
                          Billed To:
                        </h3>

                        <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                          <strong>Name:</strong> {journalVoucher?.account?.english}
                        </p>

                        <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                          <strong>Reg:</strong> {journalVoucher?.account?.registration}
                        </p>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div style={{ padding: '0', textAlign: 'right' }}>
                        <h3
                          style={{
                            fontSize: '1rem',
                            margin: '0 0 4px 0',
                            fontWeight: 'bold',
                            visibility: 'hidden',
                          }}
                        >
                          Billed To:
                        </h3>

                        <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                          <strong>Address:</strong> {journalVoucher?.account?.address}
                        </p>

                        <p style={{ margin: '1px 0', fontSize: '0.9rem' }}>
                          <strong>Phone:</strong> {journalVoucher?.account?.phone}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* ✅ Table Section */}
            <div
              className="table-wrapper"
              style={{
                flexGrow: 1, // This makes the div fill the remaining page height
                width: '100%',
                marginTop: '30px',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  background: '#fff',
                  height: '100%', // Makes the table fill the wrapper
                }}
              >
                <thead style={{ background: '#fff' }}>
                  <tr>
                    {cartColumns.map((col, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '8px',
                          border: '1px solid #000',
                          textAlign: 'left',
                          background: '#fff',
                          width: col.width ? col.width : 'auto',
                        }}
                      >
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* Render the items for the current page */}
                  {pageItems.map((item, rowIndex) => (
                    <tr key={rowIndex} style={{ background: '#fff' }}>
                      {cartColumns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          style={{
                            padding: '6px 8px',
                            borderBottom:
                              // ✅ MODIFIED: Only add border if this is the
                              // last item AND no blank rows are being added.
                              rowIndex === pageItems.length - 1 &&
                              blankRowCount === 0
                                ? '1px solid #000'
                                : 'none',
                            borderLeft: '1px solid #000',
                            borderRight: '1px solid #000',
                            verticalAlign: 'top',
                            background: '#fff',
                            width: col.width ? col.width : 'auto',
                          }}
                        >
                          {col.render
                            ? col.render(item[col.dataIndex], item)
                            : item[col.dataIndex]}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* ✅ NEW: Render blank rows to meet the MIN_ROWS minimum */}
                  {/* This replaces the old `pageItems.length === 0` block */}
                  {blankRowCount > 0 &&
                    Array.from({ length: blankRowCount }).map((_, i) => (
                      <tr key={`blank-${i}`} style={{ background: '#fff' }}>
                        {cartColumns.map((col, colIndex) => (
                          <td
                            key={colIndex}
                            style={{
                              padding: '6px 8px',
                              // Only the *last* blank row gets the bottom border
                              borderBottom:
                                i === blankRowCount - 1
                                  ? '1px solid #000'
                                  : 'none',
                              borderLeft: '1px solid #000',
                              borderRight: '1px solid #000',
                              color: 'transparent',
                            }}
                          >
                            &nbsp;
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>

                {/* ✅ Table Footer (Only on last page) */}
                {isLastPage && (
                  <tfoot style={{ background: '#fff' }}>
                    <tr>
                      {/* This cell stretches to fill all blank space */}
                      <td
                        rowSpan="5"
                        colSpan={cartColumns.length - 5}
                        style={{
                          border: '1px solid #000',
                          borderRight: 'none',
                          padding: '8px',
                          textAlign: 'left',
                          background: '#fff',
                          verticalAlign: 'top',
                          height: '100%', // This stretches the cell vertically
                          minHeight: '100px', // Fallback for empty table
                        }}
                      >
                        <p style={{ margin: '0' }}>
                          <strong>Only:</strong>{' '}
                          {toWords(journalVoucher?.totalDebit)}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          background: '#fff',
                        }}
                      >
                        <strong>Total Before Vat:</strong>&nbsp;
                        {numberFormatter(totalBeforeVat)} {mainCurrency?.symbol}
                        &nbsp;&nbsp;
                        {numberFormatter(secondaryTotalBeforeVat)}{' '}
                        {secondaryCurrency?.symbol}
                      </td>
                    </tr>

                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          background: '#fff',
                        }}
                      >
                        <strong>Discount {numberFormatter(journalVoucher?.discountPercentage)}%:</strong>&nbsp;
                        {numberFormatter(journalVoucher?.discountAmount)}{' '}
                        {mainCurrency?.symbol}
                        &nbsp;&nbsp;
                        {numberFormatter(journalVoucher?.secondaryDiscountAmount)}{' '}
                        {secondaryCurrency?.symbol}
                      </td>
                    </tr>

                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          background: '#fff',
                        }}
                      >
                        <strong>VAT 11%:</strong>&nbsp;
                        {numberFormatter(vatAmount)} {mainCurrency?.symbol}
                        &nbsp;&nbsp;
                        {numberFormatter(secondaryVatAmount)}{' '}
                        {secondaryCurrency?.symbol}
                      </td>
                    </tr>

                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          background: '#fff',
                        }}
                      >
                        <strong>Total price:</strong>&nbsp;
                        {numberFormatter(journalVoucher?.totalDebit)}{' '}
                        {mainCurrency?.symbol}
                        &nbsp;&nbsp;
                        {numberFormatter(journalVoucher?.secondaryTotalDebit)}{' '}
                        {secondaryCurrency?.symbol}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {/* End of Table Wrapper */}

            {/* ✅ Page Footer (Arabic + Signatures) */}
            <div
              className="page-footer"
              style={{
                flexShrink: 0, // Ensures this footer isn't compressed
                background: '#fff',
                paddingTop: '8px',
              }}
            >
              {/* ✅ Arabic sentence */}
              <div
                style={{
                  textAlign: 'center',
                  marginTop: '20px',
                  fontWeight: 'bold',
                }}
              >
                ان رسم الطابع المالي مسدد نقدا
              </div>

              {/* ✅ Signature Section */}
              <div
                style={{
                  marginTop: '8px',
                  paddingTop: '8px',
                  background: '#fff',
                }}
              >
                <Row>
                  <Col md={4} style={{ textAlign: 'center' }}>
                    <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                      Receiver Signature:
                    </p>
                    <p style={{ margin: '25px 0 5px 0' }}>
                      _________________________
                    </p>
                  </Col>
                  <Col md={4} style={{ textAlign: 'center' }}>
                    <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                      Sales Signature:
                    </p>
                    <p style={{ margin: '25px 0 5px 0' }}>
                      _________________________
                    </p>
                  </Col>
                  <Col md={4} style={{ textAlign: 'center' }}>
                    <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                      Accountant:
                    </p>
                    <p style={{ margin: '25px 0 5px 0' }}>
                      _________________________
                    </p>
                  </Col>
                </Row>
              </div>
            </div>

            {/* ✅ Page Number (Renders on ALL pages) */}
            <div
              style={{
                flexShrink: 0,
                textAlign: 'center',
                fontSize: '0.8rem',
                color: '#777',
                marginTop: '10px',
              }}
            >
              Page {pageIndex + 1} of {totalPages}
            </div>
          </div> // End of Page Container
        ); 
      })}
      
    </div> // End of Main Print Wrapper
  );
});

export default ComponentToPrint;